import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import {
    Description as PdfIcon,
    Image as ImageIcon,
    InsertDriveFile as FileIcon,
    Visibility as ViewIcon,
    Lock as LockIcon
} from '@mui/icons-material';

/**
 * A reusable Thumbnail component that either shows an icon or a small image preview.
 * It handles blob fetching internally if a fetcher is provided.
 */
const FileThumbnail = ({
    file, // { name, type, url, ... }
    size = 40,
    onClick,
    fetcher, // Optional function to fetch blob if URL is not public
    isRestricted = false
}) => {
    const [thumbUrl, setThumbUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const fileExt = file.name?.split('.').pop().toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(fileExt) || file.type?.startsWith('image/');
    const isPdf = fileExt === 'pdf' || file.type === 'application/pdf';

    // Use a ref for fetcher to avoid unnecessary re-runs if the function identity changes but logic is same
    const fetcherRef = React.useRef(fetcher);
    useEffect(() => {
        fetcherRef.current = fetcher;
    }, [fetcher]);

    useEffect(() => {
        let mounted = true;
        let objectUrl = null;

        const loadThumb = async () => {
            // Basic validity check
            if ((!isImage && !isPdf) || !fetcherRef.current) return;

            setLoading(true);
            try {
                const blob = await fetcherRef.current(file);
                if (!mounted) return;

                if (isImage) {
                    // Existing Image Logic
                    if (blob) {
                        objectUrl = window.URL.createObjectURL(new Blob([blob]));
                        setThumbUrl(objectUrl);
                    }
                } else if (isPdf && blob) {
                    // PDF Logic
                    try {
                        // Dynamically import pdfjs to avoid heavy bundle initial load if not needed? 
                        // For now we import at top-level or use require if possible, but let's assume top level import is fine or we do dynamic import here.
                        // Im doing dynamic import to play safe with bundle size and SSR if applicable.
                        const pdfjsLib = await import('pdfjs-dist/webpack');

                        // Fallback worker if webpack binding fails (common in CRA)
                        if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
                            pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
                        }

                        const arrayBuffer = await blob.arrayBuffer();
                        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
                        const pdf = await loadingTask.promise;
                        const page = await pdf.getPage(1);

                        // Render to canvas
                        // Scale 0.5 or based on size? size is usually small (40-56).
                        // We want high enough res to look good on retina, but small enough to be fast.
                        // Viewport width of ~150px is plenty for a thumbnail.
                        const viewport = page.getViewport({ scale: 1 });
                        const scale = 150 / viewport.width;
                        const scaledViewport = page.getViewport({ scale });

                        const canvas = document.createElement('canvas');
                        const context = canvas.getContext('2d');
                        canvas.height = scaledViewport.height;
                        canvas.width = scaledViewport.width;

                        const renderContext = {
                            canvasContext: context,
                            viewport: scaledViewport,
                        };

                        await page.render(renderContext).promise;

                        if (mounted) {
                            objectUrl = canvas.toDataURL();
                            setThumbUrl(objectUrl);
                        }
                    } catch (pdfErr) {
                        console.error('PDF Render Error:', pdfErr);
                        // Fallback to placeholder if PDF render fails
                        setThumbUrl(null);
                    }
                }

            } catch (err) {
                console.error('Failed to load thumbnail:', err);
                if (mounted) setError(true);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        loadThumb();

        return () => {
            mounted = false;
            // Revoke blob URL if it was created via createObjectURL
            // If it's a data URL (from canvas), we don't strictly need to revoke, but good hygiene
            if (objectUrl && !objectUrl.startsWith('data:')) {
                window.URL.revokeObjectURL(objectUrl);
            }
        };
        // Unstable dependencies removed. We implicitly trust that if file.name/type are same, it's the same file.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file.name, file.type, isImage, isPdf]);

    const getFileColor = (ext) => {
        const colors = {
            pdf: '#ef4444',
            doc: '#3b82f6',
            docx: '#3b82f6',
            xls: '#22c55e',
            xlsx: '#22c55e',
            ppt: '#f97316',
            pptx: '#f97316',
            txt: '#64748b'
        };
        return colors[ext] || '#94a3b8';
    };

    const renderPlaceholder = () => {
        const color = getFileColor(fileExt);
        const LabelIcon = isPdf ? PdfIcon : FileIcon;

        return (
            <Box sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                bgcolor: '#ffffff',
            }}>
                {/* Mock Content Layout - using % for responsive scalability within the thumbnail box */}
                <Box sx={{
                    p: '10%',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6%',
                    opacity: 0.8
                }}>
                    {/* Header/Title block */}
                    <Box sx={{ width: '40%', height: '15%', bgcolor: '#94a3b8', borderRadius: '15%' }} />

                    {/* Text lines */}
                    <Box sx={{ width: '85%', height: '8%', bgcolor: '#e2e8f0', borderRadius: '10%' }} />
                    <Box sx={{ width: '90%', height: '8%', bgcolor: '#e2e8f0', borderRadius: '10%' }} />
                    <Box sx={{ width: '70%', height: '8%', bgcolor: '#e2e8f0', borderRadius: '10%' }} />

                    {/* Image/Media block placeholder */}
                    <Box sx={{ width: '100%', height: '25%', bgcolor: '#f1f5f9', borderRadius: '8%', mt: 'auto' }} />
                </Box>

                {/* File Type Badge */}
                <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: color,
                    color: 'white',
                    fontSize: Math.max(8, size * 0.22) + 'px', // Dynamic font size based on prop size
                    fontWeight: 'bold',
                    px: '6px',
                    py: '2px',
                    borderTopLeftRadius: 6,
                    textTransform: 'uppercase',
                    // boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}>
                    {fileExt || 'DOC'}
                </Box>
            </Box>
        );
    };

    return (
        <Tooltip title={`Click to preview ${file.name}`}>
            <Box
                onClick={onClick}
                sx={{
                    width: size,
                    height: size,
                    borderRadius: 1.5,
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    transition: 'all 0.2s',
                    border: '1px solid rgba(0,0,0,0.1)',
                    position: 'relative',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        '& .hover-overlay': { opacity: 1 }
                    }
                }}
            >
                {thumbUrl ? (
                    <img
                        src={thumbUrl}
                        alt={file.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    renderPlaceholder()
                )}

                {loading && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255,255,255,0.5)',
                        zIndex: 1
                    }}>
                        <CircularProgress size={size * 0.4} thickness={5} />
                    </Box>
                )}



                <Box
                    className="hover-overlay"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        zIndex: 1
                    }}
                >
                    <ViewIcon sx={{ fontSize: size * 0.5 }} />
                </Box>
            </Box>
        </Tooltip>
    );
};

export default FileThumbnail;
